import CryptoJS from 'crypto-js';

class StorageService {
  constructor() {
    this.WALLET_KEY = 'mytherra_wallet_encrypted';
    this.EMAIL_KEY = 'mytherra_user_email';
    this.SESSION_KEY = 'mytherra_session';
    this.RPC_CONFIG_KEY = 'mytherra_rpc_config';
  }

  /**
   * Save encrypted wallet
   */
  saveWallet(privateKeyWif, password) {
    try {
      // Encrypt private key with password
      const encrypted = CryptoJS.AES.encrypt(privateKeyWif, password).toString();
      localStorage.setItem(this.WALLET_KEY, encrypted);
      return true;
    } catch (e) {
      console.error('Error saving wallet:', e);
      return false;
    }
  }

  /**
   * Load and decrypt wallet
   */
  loadWallet(password) {
    try {
      const encrypted = localStorage.getItem(this.WALLET_KEY);
      if (!encrypted) return null;

      // Decrypt with password
      const decrypted = CryptoJS.AES.decrypt(encrypted, password);
      const privateKey = decrypted.toString(CryptoJS.enc.Utf8);

      // Verify it's not empty (wrong password)
      if (!privateKey || privateKey.length === 0) {
        return null;
      }

      return privateKey;
    } catch (e) {
      console.error('Error loading wallet:', e);
      return null;
    }
  }

  /**
   * Check if user has an account
   */
  hasUserAccount() {
    return localStorage.getItem(this.WALLET_KEY) !== null;
  }

  /**
   * Delete wallet
   */
  deleteWallet() {
    localStorage.removeItem(this.WALLET_KEY);
    localStorage.removeItem(this.EMAIL_KEY);
    localStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Save user email
   */
  saveUserEmail(email) {
    localStorage.setItem(this.EMAIL_KEY, email);
  }

  /**
   * Load user email
   */
  loadUserEmail() {
    return localStorage.getItem(this.EMAIL_KEY);
  }

  /**
   * Save session (temporary unencrypted private key)
   * SECURITY NOTE: This is stored temporarily for convenience
   * User should logout when done
   */
  saveSession(privateKeyWif) {
    sessionStorage.setItem(this.SESSION_KEY, privateKeyWif);
  }

  /**
   * Load session
   */
  loadSession() {
    return sessionStorage.getItem(this.SESSION_KEY);
  }

  /**
   * Clear session
   */
  clearSession() {
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Check if session exists
   */
  hasSession() {
    return sessionStorage.getItem(this.SESSION_KEY) !== null;
  }

  /**
   * Save RPC configuration
   */
  saveRpcConfig(url, user, password) {
    const config = { url, user, password };
    localStorage.setItem(this.RPC_CONFIG_KEY, JSON.stringify(config));
  }

  /**
   * Load RPC configuration
   */
  loadRpcConfig() {
    try {
      const configStr = localStorage.getItem(this.RPC_CONFIG_KEY);
      if (!configStr) return null;
      return JSON.parse(configStr);
    } catch (e) {
      console.error('Error loading RPC config:', e);
      return null;
    }
  }

  /**
   * Delete RPC configuration
   */
  deleteRpcConfig() {
    localStorage.removeItem(this.RPC_CONFIG_KEY);
  }

  /**
   * Export wallet backup (encrypted)
   */
  exportWalletBackup() {
    const wallet = localStorage.getItem(this.WALLET_KEY);
    const email = localStorage.getItem(this.EMAIL_KEY);
    
    if (!wallet) return null;

    const backup = {
      version: '1.0',
      timestamp: new Date().toISOString(),
      wallet: wallet,
      email: email || '',
    };

    return JSON.stringify(backup, null, 2);
  }

  /**
   * Import wallet backup
   */
  importWalletBackup(backupJson) {
    try {
      const backup = JSON.parse(backupJson);
      
      if (!backup.wallet) {
        throw new Error('Invalid backup file');
      }

      localStorage.setItem(this.WALLET_KEY, backup.wallet);
      if (backup.email) {
        localStorage.setItem(this.EMAIL_KEY, backup.email);
      }

      return true;
    } catch (e) {
      console.error('Error importing backup:', e);
      return false;
    }
  }

  /**
   * Get wallet status
   */
  getWalletStatus() {
    return {
      hasWallet: this.hasUserAccount(),
      hasSession: this.hasSession(),
      email: this.loadUserEmail(),
    };
  }
}

// Export singleton instance
export default new StorageService();
